import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getCurrentUserId, getAccessToken } from "../awsAuth";
import { useAuth } from "./AuthContext";

const ThemeContext = createContext();

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const apiUrl = (path) =>
  path.startsWith("http://") || path.startsWith("https://")
    ? path
    : `${API_BASE || ""}${path}`;

export const ThemeProvider = ({ children }) => {
  const { isLoggedIn, userId } = useAuth() || {};
  const [theme, setTheme] = useState("dark");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    try {
      const saved = localStorage.getItem("cvanto_theme");
      const next = saved === "dark" || saved === "light" ? saved : "dark";
      if (alive) setTheme(next);
    } catch {
      if (alive) setTheme("dark");
    } finally {
      if (alive) setLoading(false);
    }

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadThemeFromAccount = async () => {
      const uid = userId || getCurrentUserId?.();

      if (!isLoggedIn || !uid) return;

      try {
        const token = await getAccessToken?.();

        const res = await fetch(apiUrl(`/api/account/${uid}`), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!res.ok) return;

        const data = await res.json();
        const serverTheme =
          data?.theme === "dark" || data?.theme === "light" ? data.theme : "dark";

        if (cancelled) return;

        setTheme(serverTheme);

        try {
          localStorage.setItem("cvanto_theme", serverTheme);
        } catch {}
      } catch {}
    };

    loadThemeFromAccount();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId]);

  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
    document.body.classList.remove("light-theme", "dark-theme");
    document.body.classList.add(`${theme}-theme`);
  }, [theme]);

  const setAndPersistTheme = useCallback(
    async (newTheme) => {
      const resolved = newTheme === "light" ? "light" : "dark";
      setTheme(resolved);

      try {
        localStorage.setItem("cvanto_theme", resolved);
      } catch {}

      const uid = userId || getCurrentUserId?.();
      if (!uid) return;

      try {
        const token = await getAccessToken?.();

        await fetch(apiUrl(`/api/account/${uid}`), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ theme: resolved }),
        });
      } catch {}
    },
    [userId]
  );

  const toggleTheme = useCallback(() => {
    setAndPersistTheme(theme === "light" ? "dark" : "light");
  }, [theme, setAndPersistTheme]);

  return (
    <ThemeContext.Provider
      value={{ theme, toggleTheme, setTheme: setAndPersistTheme, loading }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);